import React from 'react';
import styles from './RadioButton.module.css';

/**
 * A premium custom RadioButton selection component.
 * Supports customized active states, hex color themes, side labels, group usage, and disabled modes.
 *
 * @component
 * @param {Object} props
 * @param {(value?: any) => void} [props.onClick] - Callback fired when clicked. Passes the value of the radio button.
 * @param {string} [props.label] - Side label text displayed next to the circular selection indicator.
 * @param {boolean} [props.checked=false] - Controls whether this radio button is selected.
 * @param {any} [props.value] - The value associated with this radio button (used inside groups).
 * @param {string} [props.color] - Custom accent hex color applied when selected/active.
 * @param {boolean} [props.disabled=false] - If true, disables clicks and styles as muted/inactive.
 */
const RadioButton = ({ onClick, label, checked, value, color, disabled = false }) => {
  const handleClick = () => {
    if (disabled) return;
    onClick?.(value);
  };

  // Draw the ring border inline so a host app's unlayered CSS reset (e.g. a Meyer
  // reset with `div { border: 0 }`) can't strip it — inline styles win over the
  // @layer'd `.radioBox` rule in the cascade. Colors still resolve from theme vars.
  const ringColor = checked
    ? (color || 'var(--ca-theme-palette-primary-main, #4b1c46)')
    : 'var(--ca-theme-palette-divider, #968896)';
  const boxStyle    = { border: `1px solid ${ringColor}` };
  const dotStyle    = (checked && color) ? { backgroundColor: color } : undefined;

  return (
    <div
      className={[styles.wrapper, disabled && styles.disabled].filter(Boolean).join(' ')}
      onClick={handleClick}
    >
      <div className={[styles.radioBox, checked && styles.checked].filter(Boolean).join(' ')} style={boxStyle}>
        <div className={[styles.radioBoxInternal, checked && styles.checked].filter(Boolean).join(' ')} style={dotStyle} />
      </div>
      {/* Inline font-size for the same reason as the ring border: a host app's
          unlayered `span { font: inherit }` reset would otherwise override the
          @layer'd `.label` size. */}
      {label && <span className={styles.label} style={{ fontSize: '0.875rem' }}>{label}</span>}
    </div>
  );
};

export default RadioButton;
